import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  Upload,
  FileText,
  Trash2,
  Music,
  Loader2,
  Database,
  HardDrive,
  ArrowRightLeft,
  Eye,
  Pencil,
  AlertTriangle,
  FolderOpen,
  History,
  ChevronDown,
} from "lucide-react";
import ExportDropdown from "../Shared/ExportDropdown";
import AudioPlayer from "../Audio/AudioPlayer";
import AudioDropdownMenu from "../Audio/AudioDropdownMenu";
import TakesList from "../Audio/TakesList";
import MixToolbar from "../Audio/MixToolbar";
import audioStorageService from "../../services/audioStorageService";
import FilterBar from "../Organization/FilterBar";
import useLoopRecord from "../../hooks/useLoopRecord";

const UploadTab = ({
  songs,
  onFileUpload,
  onDeleteSong,
  onSongSelect,
  onEditSong,
  onExportTxt,
  onExportPdf,
  isDragging,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  darkMode,
  // Audio-related props
  onAudioUpload = null,
  onAudioDownload = null,
  onAudioRemove = null,
  userId = null,
  // Storage type props
  storageType = "local",
  onStorageTypeChange = null,
  isAuthenticated = false,
  // Transfer props
  onTransferSong = null,
  // Draft-related props (optional)
  onCreateDraft = null, // eslint-disable-line @typescript-eslint/no-unused-vars
  onDeleteDraft = null, // eslint-disable-line @typescript-eslint/no-unused-vars
  onOpenDraft = null, // eslint-disable-line @typescript-eslint/no-unused-vars
  MAX_DRAFTS_PER_SONG = 5, // eslint-disable-line @typescript-eslint/no-unused-vars
  // Audio player expansion state (passed from parent for persistence)
  expandedAudioSongId = null,
  setExpandedAudioSongId = null,
  // Unsaved changes warning
  hasUnsavedChanges = false,
  // Organization props
  projects = [],
  tags = [],
  activeProjectId = null,
  activeTagIds = [],
  songCounts = {},
  totalSongCount = 0,
  onProjectFilter = null,
  onTagFilter = null,
  onClearFilters = null,
  onAssignSong = null,
  // Version history props
  onOpenVersionPanel = null,
  // Recording props
  recorder = null,
  takeManager = null,
  onSaveTake = null,
  isMobile = false,
  // Pro flag
  isPro = false,
  // Teleprompter props
  onOpenTeleprompter = null,
  teleprompterSongId = null,
  // Loop-record state callback (notifies parent so FloatingNotepad can suppress save-take dialog)
  onLoopRecordActiveChange = null,
  // Takes-record state callback (notifies parent so FloatingNotepad can suppress save-take dialog)
  onTakesRecordActiveChange = null,
}) => {
  // Ref for the hidden audio file input
  const audioInputRef = useRef(null);
  // Track which song we're uploading audio for
  const [uploadingSongId, setUploadingSongId] = useState(null);
  // Track upload progress
  const [isUploading, setIsUploading] = useState(false);
  // Storage switch warning modal
  const [showStorageWarning, setShowStorageWarning] = useState(false);
  const [pendingStorageType, setPendingStorageType] = useState(null);
  // Record/Takes panel state per song
  const [takesSongId, setTakesSongId] = useState(null);
  // WaveSurfer instance refs for MixToolbar (Play Both / Export Mix)
  const [beatWaveSurfer, setBeatWaveSurfer] = useState(null);
  const [takeWaveSurfer, setTakeWaveSurfer] = useState(null);
  // Auto-play beat when recording starts
  const [autoPlayBeat, setAutoPlayBeat] = useState(false);
  const pendingAutoPlayRef = useRef(false);
  // Loop state for takes player
  const [loopActive, setLoopActive] = useState(false);
  // Loop region — driven by TakesList A-B markers, passed to useLoopRecord
  const [loopRegion, setLoopRegion] = useState(null);
  // Beat duration — tracked reactively from beatWaveSurfer for visual shift alignment
  const [beatDuration, setBeatDuration] = useState(0);
  // Loop-record hook — instantiated here where beatWaveSurfer and takeWaveSurfer are available
  const getTakeAudioBuffer = useCallback(async () => {
    if (!takeManager) throw new Error("Take manager not available");
    const activeTake = takeManager.getActiveTake();
    if (!activeTake) throw new Error("No active take");
    const url = await takeManager.getTakeUrl(activeTake);
    if (!url) throw new Error("Failed to get take audio URL");
    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const buffer = await audioCtx.decodeAudioData(arrayBuffer);
    audioCtx.close();
    return buffer;
  }, [takeManager]);

  const loopRecord = useLoopRecord({
    takeWaveSurfer,
    beatWaveSurfer,
    recorder,
    getTakeAudioBuffer,
    loopRegion,
    shiftOffsetSec: takeManager?.getActiveTake()?.shiftOffsetSec || 0,
  });

  // Notify parent when a loop-record is in progress (so FloatingNotepad can suppress save-take dialog)
  useEffect(() => {
    if (onLoopRecordActiveChange)
      onLoopRecordActiveChange(loopRecord.loopRecordState !== "idle");
  }, [loopRecord.loopRecordState, onLoopRecordActiveChange]);

  // Notify parent when takes panel is open (so FloatingNotepad can suppress save-take dialog)
  useEffect(() => {
    if (onTakesRecordActiveChange) onTakesRecordActiveChange(!!takesSongId);
  }, [takesSongId, onTakesRecordActiveChange]);

  // Filter bar visibility toggle — default open, persisted to localStorage
  const [showFilterBar, setShowFilterBar] = useState(() => {
    const saved = localStorage.getItem("lyrical-toolkit-filter-bar-visible");
    return saved === null ? true : saved === "true";
  });
  const toggleFilterBar = () => {
    setShowFilterBar((prev) => {
      localStorage.setItem("lyrical-toolkit-filter-bar-visible", String(!prev));
      return !prev;
    });
  };

  // Auto-play beat when recording starts (if autoPlayBeat is on)
  useEffect(() => {
    if (!recorder?.isRecording) {
      pendingAutoPlayRef.current = false;
      return;
    }
    // Recording just started — check if we should auto-play the beat
    if (!autoPlayBeat) return;

    // Find the song that currently has the audio player open
    const song = songs.find((s) => s.id === expandedAudioSongId);
    if (!song?.audioFileUrl) return;

    if (beatWaveSurfer) {
      // WaveSurfer is ready — seek to start and play
      try {
        beatWaveSurfer.seekTo(0);
        beatWaveSurfer.play();
      } catch (e) {
        console.warn("⚠️ Auto-play beat failed:", e);
      }
    } else {
      // WaveSurfer not loaded yet — expand the player and set pending flag
      if (setExpandedAudioSongId && expandedAudioSongId !== song.id) {
        setExpandedAudioSongId(song.id);
      }
      pendingAutoPlayRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recorder?.isRecording]);

  // When beatWaveSurfer becomes available and we have a pending auto-play
  useEffect(() => {
    if (beatWaveSurfer && pendingAutoPlayRef.current) {
      try {
        beatWaveSurfer.seekTo(0);
        beatWaveSurfer.play();
      } catch (e) {
        console.warn("⚠️ Pending auto-play beat failed:", e);
      }
      pendingAutoPlayRef.current = false;
    }
  }, [beatWaveSurfer]);

  // Track beat duration reactively from beatWaveSurfer
  useEffect(() => {
    if (!beatWaveSurfer) {
      setBeatDuration(0);
      return;
    }
    const update = () => setBeatDuration(beatWaveSurfer.getDuration());
    beatWaveSurfer.on("ready", update);
    if (beatWaveSurfer.getDuration() > 0) update();
    return () => beatWaveSurfer.un("ready", update);
  }, [beatWaveSurfer]);

  // Handle storage type change with unsaved changes warning
  const handleStorageSwitch = (newStorageType) => {
    if (newStorageType === storageType) return;
    if (hasUnsavedChanges) {
      setPendingStorageType(newStorageType);
      setShowStorageWarning(true);
    } else {
      onStorageTypeChange && onStorageTypeChange(newStorageType);
    }
  };

  const confirmStorageSwitch = () => {
    if (pendingStorageType && onStorageTypeChange) {
      onStorageTypeChange(pendingStorageType);
    }
    setShowStorageWarning(false);
    setPendingStorageType(null);
  };

  const cancelStorageSwitch = () => {
    setShowStorageWarning(false);
    setPendingStorageType(null);
  };

  // Handle clicking the music icon - directly open file picker
  const handleMusicIconClick = (song) => {
    if (isUploading) return;
    setUploadingSongId(song.id);
    // Trigger the hidden file input
    if (audioInputRef.current) {
      audioInputRef.current.click();
    }
  };

  // Handle audio file selection
  const handleAudioFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadingSongId) {
      setUploadingSongId(null);
      return;
    }

    // Validate the file
    const validation = audioStorageService.validateAudioFile(file);
    if (!validation.isValid) {
      alert(`Invalid audio file: ${validation.errors.join(", ")}`);
      setUploadingSongId(null);
      e.target.value = "";
      return;
    }

    // Pre-check audio limit before uploading (database mode only)
    if (storageType === "database" && userId && userId !== "anonymous") {
      try {
        const limitCheck = await audioStorageService.checkAudioLimit(userId);
        if (!limitCheck.allowed) {
          alert(
            `Audio file limit reached (${limitCheck.current}/${limitCheck.limit}).\n\nUpgrade to Pro to upload up to 50 audio files!`,
          );
          setUploadingSongId(null);
          e.target.value = "";
          return;
        }
      } catch (limitError) {
        console.error("Error checking audio limit:", limitError);
      }
    }

    setIsUploading(true);

    try {
      // Upload the file - pass storageType to use correct storage backend
      const result = await audioStorageService.uploadAudioFile(
        file,
        uploadingSongId,
        userId || "anonymous",
        storageType, // 'local' uses IndexedDB, 'database' uses Supabase Storage
      );

      // Call the parent's upload success handler
      if (onAudioUpload) {
        await onAudioUpload(uploadingSongId, result);
      }

      // Auto-expand the audio player after successful upload
      // This ensures the user sees the player immediately on Android
      if (setExpandedAudioSongId) {
        setExpandedAudioSongId(uploadingSongId);
      }
    } catch (error) {
      console.error("Audio upload error:", error);
      if (error.message && error.message.includes("limit")) {
        alert(
          `Audio file limit reached.\n\nUpgrade to Pro to upload up to 50 audio files!`,
        );
      } else {
        alert(`Failed to upload audio: ${error.message}`);
      }
    } finally {
      setIsUploading(false);
      setUploadingSongId(null);
      // Clear the input for re-upload
      e.target.value = "";
    }
  };

  return (
    <div>
      {/* Storage Switch Warning Modal */}
      {showStorageWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="max-w-md w-full mx-4 p-6 rounded-lg shadow-xl bg-forest-800">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-gold-400" />
              <h3 className="text-lg font-semibold text-forest-100">
                Unsaved Changes
              </h3>
            </div>
            <p className="mb-6 text-forest-200">
              You have unsaved changes in the notepad. Switching storage modes
              may cause you to lose your work. Are you sure you want to
              continue?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelStorageSwitch}
                className="px-4 py-2 rounded-lg transition-colors bg-forest-700 hover:bg-forest-600 text-forest-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmStorageSwitch}
                className="px-4 py-2 rounded-lg transition-colors bg-gold-500 hover:bg-gold-400 text-forest-900"
              >
                Switch Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden audio file input */}
      <input
        ref={audioInputRef}
        type="file"
        accept="audio/*,.mp3,.wav,.m4a,.mp4,.aac,.ogg,.flac"
        onChange={handleAudioFileSelect}
        className="hidden"
        disabled={isUploading}
      />

      {/* Hidden .txt file input */}
      <input
        type="file"
        multiple
        accept=".txt"
        onChange={(e) => onFileUpload(Array.from(e.target.files))}
        className="hidden"
        id="file-upload"
      />

      {/* Storage Type Tabs */}
      <div>
        <div className="flex gap-1 mb-4 p-1 rounded-lg bg-forest-800 md:justify-center md:gap-2">
          <button
            onClick={() => handleStorageSwitch("local")}
            style={{ flex: "1 1 0%" }}
            className={`flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              storageType === "local"
                ? "bg-forest-700 text-forest-100"
                : "text-forest-300 hover:text-forest-200"
            }`}
          >
            <HardDrive className="w-4 h-4" />
            Local
          </button>
          <button
            onClick={() => handleStorageSwitch("database")}
            disabled={!isAuthenticated}
            style={{ flex: "1 1 0%" }}
            className={`flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              storageType === "database"
                ? "bg-forest-700 text-forest-100"
                : !isAuthenticated
                  ? "text-forest-300 cursor-not-allowed opacity-60"
                  : "text-forest-300 hover:text-forest-200"
            }`}
            title={
              !isAuthenticated ? "Login required for database storage" : ""
            }
          >
            <Database className="w-4 h-4" />
            {isAuthenticated ? "Cloud" : "Cloud (Login)"}
          </button>
        </div>

        {/* Info Banner */}
        <div className="mb-4 p-3 rounded-lg text-sm bg-forest-900/50 text-forest-200 border border-forest-700">
          {storageType === "local" ? (
            <p>
              <strong>Local:</strong> Songs are stored on this device only.
            </p>
          ) : (
            <p>
              <strong>Cloud:</strong> Songs are synced to the cloud. Access them
              from any device when logged in.
            </p>
          )}
        </div>
      </div>

      {/* Empty state — no songs yet */}
      {songs.length === 0 && totalSongCount === 0 && (
        <div className="mt-4 text-center py-8">
          <p className="text-sm text-forest-400 mb-3">
            No songs yet. Start writing in the notepad or upload a .txt file.
          </p>
          <label
            htmlFor="file-upload"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg cursor-pointer transition-colors text-sm bg-forest-700 hover:bg-forest-600 text-forest-100"
          >
            <Upload className="w-4 h-4" />
            Upload
          </label>
        </div>
      )}

      {/* Uploaded Songs List */}
      {(songs.length > 0 || totalSongCount > 0) && (
        <div className="mt-4">
          <div
            className={`flex items-center mb-4 ${isMobile ? "justify-between" : "gap-3"}`}
          >
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-medium text-forest-100">
                Your Songs ({songs.length})
              </h3>
              {(projects.length > 0 || tags.length > 0) &&
                onProjectFilter &&
                onTagFilter && (
                  <button
                    onClick={toggleFilterBar}
                    className="rounded-lg transition-colors bg-forest-700 hover:bg-forest-600 text-forest-100"
                    title={showFilterBar ? "Hide filters" : "Show filters"}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "6px 8px",
                      fontSize: "0.875rem",
                      lineHeight: "1.25rem",
                    }}
                  >
                    <ChevronDown
                      style={{
                        width: "14px",
                        height: "14px",
                        transform: showFilterBar
                          ? "rotate(0deg)"
                          : "rotate(-90deg)",
                        transition: "transform 0.2s ease",
                      }}
                    />
                  </button>
                )}
            </div>
            <label
              htmlFor="file-upload"
              className="inline-flex items-center gap-1.5 px-3 rounded-lg cursor-pointer transition-colors text-sm bg-forest-700 hover:bg-forest-600 text-forest-100"
              style={{
                paddingTop: "4.5px",
                paddingBottom: "4.5px",
                border: "1px solid var(--color-forest-500, #4a5c4a)",
              }}
            >
              <Upload style={{ width: "13px", height: "13px" }} />
              Upload
            </label>
          </div>

          {/* Organization Filter Bar */}
          {showFilterBar &&
            (projects.length > 0 || tags.length > 0) &&
            onProjectFilter &&
            onTagFilter && (
              <FilterBar
                projects={projects}
                tags={tags}
                activeProjectId={activeProjectId}
                activeTagIds={activeTagIds}
                songCounts={songCounts}
                onProjectSelect={onProjectFilter}
                onTagToggle={onTagFilter}
                onClearFilters={onClearFilters}
                totalSongCount={totalSongCount}
                isMobile={isMobile}
              />
            )}

          {/* Empty state when filters produce no results */}
          {songs.length === 0 && totalSongCount > 0 && (
            <p className="text-sm text-forest-400 text-center py-8">
              No songs match the current filter.
            </p>
          )}

          <div className="grid gap-3">
            {songs.map((song) => (
              <div
                key={song.id}
                className="rounded-lg border p-4 transition-colors bg-forest-800 border-forest-700 card-glow"
              >
                <div className="song-card-layout flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                  <div
                    className="flex items-center min-w-0"
                    style={isMobile ? { width: "100%" } : undefined}
                  >
                    <FileText className="w-5 h-5 mr-3 flex-shrink-0 text-forest-300" />
                    <div style={{ flex: "1 1 0%", minWidth: 0 }}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-medium text-forest-100">
                          {song.title}
                        </h4>
                        {song.isExample && (
                          <span className="text-xs px-2 py-1 rounded-full bg-forest-900 text-forest-200 border border-forest-700">
                            Example
                          </span>
                        )}
                        {song.audioFileUrl && (
                          <span className="text-xs px-2 py-1 rounded-full flex items-center gap-1 bg-leaf-900 text-leaf-200 border border-leaf-700">
                            <Music className="w-3 h-3" />
                            Audio
                          </span>
                        )}
                        {!isMobile && song.projectName && (
                          <span
                            className="text-xs px-2 py-0.5 rounded bg-forest-900 text-forest-300 border border-forest-700"
                            style={{
                              borderLeftWidth: "3px",
                              borderLeftColor: song.projectColor || "#6b7280",
                            }}
                          >
                            {song.projectName}
                          </span>
                        )}
                        {!isMobile &&
                          song.tags &&
                          song.tags.length > 0 &&
                          song.tags.filter(Boolean).map((tag) => (
                            <span
                              key={tag.id}
                              className="text-xs px-2 py-0.5 rounded-full border"
                              style={{
                                borderColor: tag.color || "#4a5c4a",
                                color: tag.color || "#a3b8a3",
                              }}
                            >
                              #{tag.name}
                            </span>
                          ))}
                      </div>
                      <p className="text-sm text-forest-300">
                        {song.wordCount} words • Added{" "}
                        {new Date(song.dateAdded).toLocaleDateString()}
                      </p>
                      {isMobile &&
                        (song.projectName ||
                          (song.tags && song.tags.length > 0)) && (
                          <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide mt-1">
                            {song.projectName && (
                              <span
                                className="flex-shrink-0 whitespace-nowrap text-xs px-2 py-0.5 rounded bg-forest-900 text-forest-300 border border-forest-700"
                                style={{
                                  borderLeftWidth: "3px",
                                  borderLeftColor:
                                    song.projectColor || "#6b7280",
                                }}
                              >
                                {song.projectName}
                              </span>
                            )}
                            {song.tags &&
                              song.tags.length > 0 &&
                              song.tags.filter(Boolean).map((tag) => (
                                <span
                                  key={tag.id}
                                  className="flex-shrink-0 whitespace-nowrap text-xs px-2 py-0.5 rounded-full border"
                                  style={{
                                    borderColor: tag.color || "#4a5c4a",
                                    color: tag.color || "#a3b8a3",
                                  }}
                                >
                                  #{tag.name}
                                </span>
                              ))}
                          </div>
                        )}
                    </div>
                    {/* Delete — mobile only, far right of header row */}
                    {isMobile && (
                      <button
                        onClick={() => onDeleteSong(song.id)}
                        className="rounded transition-colors bg-ember-500 hover:bg-ember-400 text-forest-900"
                        title="Delete song"
                        style={{
                          flexShrink: 0,
                          alignSelf: "center",
                          marginLeft: "auto",
                          padding: "3px 5px",
                          lineHeight: 1,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Trash2 style={{ width: 12, height: 12 }} />
                      </button>
                    )}
                  </div>
                  <div className="song-card-buttons flex items-center gap-1 md:gap-2 flex-nowrap md:justify-end">
                    {/* Audio dropdown menu — replaces separate Audio + Music buttons */}
                    <AudioDropdownMenu
                      song={song}
                      isMobile={isMobile}
                      hasAudio={!!song.audioFileUrl}
                      isPlayerOpen={expandedAudioSongId === song.id}
                      isRecordOpen={takesSongId === song.id}
                      onTogglePlayer={() => {
                        if (setExpandedAudioSongId) {
                          setExpandedAudioSongId(
                            expandedAudioSongId === song.id ? null : song.id,
                          );
                        }
                      }}
                      onToggleRecord={() => {
                        if (takesSongId === song.id) {
                          setTakesSongId(null);
                        } else {
                          setTakesSongId(song.id);
                          if (takeManager) takeManager.loadTakes(song.id);
                        }
                      }}
                      onUploadMusic={() => handleMusicIconClick(song)}
                      isUploading={isUploading && uploadingSongId === song.id}
                      // Teleprompter
                      isTeleprompterOpen={teleprompterSongId === song.id}
                      onToggleTeleprompter={
                        onOpenTeleprompter ? (s) => onOpenTeleprompter(s) : null
                      }
                      hasTeleprompter={
                        !!(song.content && song.content.trim().length > 0)
                      }
                    />
                    <button
                      onClick={() => onSongSelect(song)}
                      className="text-xs px-0.5 py-0.5 md:px-2 md:py-1 rounded transition-colors bg-forest-700 hover:bg-forest-600 text-forest-200 inline-flex items-center gap-1"
                      title="View lyrics"
                    >
                      <Eye className="w-3 h-3 md:w-4 md:h-4 text-gold-500" />
                      <span className="hidden md:inline">View</span>
                    </button>
                    <button
                      onClick={() => onEditSong(song)}
                      className="text-xs px-0.5 py-0.5 md:px-2 md:py-1 rounded transition-colors bg-forest-700 hover:bg-forest-600 text-forest-200 inline-flex items-center gap-1"
                      title="Edit song"
                    >
                      <Pencil className="w-3 h-3 md:w-4 md:h-4 text-gold-500" />
                      <span className="hidden md:inline">Edit</span>
                    </button>
                    {/* History - now on row 1 for both desktop and mobile */}
                    {onOpenVersionPanel && (
                      <button
                        onClick={() => onOpenVersionPanel(song)}
                        className="text-xs px-0.5 py-0.5 md:px-2 md:py-1 rounded transition-colors bg-forest-700 hover:bg-forest-600 text-forest-200 inline-flex items-center gap-1"
                        title="Version history"
                      >
                        <History className="w-3 h-3 md:w-4 md:h-4 text-gold-500" />
                        <span className="hidden md:inline">History</span>
                      </button>
                    )}
                    {/* Organize - now on row 1 for both desktop and mobile */}
                    {onAssignSong && (
                      <button
                        onClick={() => onAssignSong(song)}
                        className="text-xs px-0.5 py-0.5 md:px-2 md:py-1 rounded transition-colors bg-forest-700 hover:bg-forest-600 text-forest-200 inline-flex items-center gap-1"
                        title="Organize song"
                      >
                        <FolderOpen className="w-3 h-3 md:w-4 md:h-4 text-gold-500" />
                        <span className="hidden md:inline">Organize</span>
                      </button>
                    )}
                    <ExportDropdown
                      song={song}
                      onExportTxt={onExportTxt}
                      onExportPdf={onExportPdf}
                      isMobile={isMobile}
                    />
                    {/* Transfer button - only show for non-example songs */}
                    {!song.isExample && onTransferSong && (
                      <button
                        onClick={() => onTransferSong(song)}
                        disabled={storageType === "local" && !isAuthenticated}
                        className={`text-xs px-0.5 py-0.5 md:px-2 md:py-1 rounded transition-colors inline-flex items-center gap-1 ${
                          storageType === "local" && !isAuthenticated
                            ? "bg-forest-700 cursor-not-allowed opacity-50 text-forest-200"
                            : "bg-forest-700 hover:bg-forest-600 text-forest-200"
                        }`}
                        title={
                          storageType === "local" && !isAuthenticated
                            ? "Login required to transfer to cloud"
                            : storageType === "local"
                              ? "Transfer to Cloud"
                              : "Transfer to Local"
                        }
                      >
                        <ArrowRightLeft className="w-3 h-3 md:w-4 md:h-4 text-gold-500" />
                        <span className="hidden md:inline">Transfer</span>
                      </button>
                    )}
                    {/* Delete — desktop only */}
                    {!isMobile && (
                      <button
                        onClick={() => onDeleteSong(song.id)}
                        className="text-xs px-0.5 py-0.5 md:px-2 md:py-1 rounded transition-colors bg-ember-500 hover:bg-ember-400 text-forest-900 inline-flex items-center gap-1"
                        title="Delete song"
                      >
                        <Trash2 className="w-3 h-3 md:w-4 md:h-4" />
                        <span className="hidden md:inline">Delete</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Audio sections: Player, Mix Toolbar, Record/Takes */}
                {(expandedAudioSongId === song.id ||
                  takesSongId === song.id) && (
                  <div className="mt-4 pt-4 border-t border-forest-600">
                    {/* Audio Player */}
                    {expandedAudioSongId === song.id && song.audioFileUrl && (
                      <AudioPlayer
                        audioUrl={song.audioFileUrl}
                        audioFilename={song.audioFileName}
                        audioSize={song.audioFileSize}
                        audioDuration={song.audioDuration}
                        darkMode={darkMode}
                        onDownload={() =>
                          onAudioDownload && onAudioDownload(song)
                        }
                        onRemove={() => onAudioRemove && onAudioRemove(song.id)}
                        onReplace={() => handleMusicIconClick(song)}
                        showControls={true}
                        compact={false}
                        onWaveSurferReady={setBeatWaveSurfer}
                      />
                    )}

                    {/* Mix Toolbar — between player and takes when both open */}
                    {expandedAudioSongId === song.id &&
                      takesSongId === song.id && (
                        <MixToolbar
                          beatWaveSurfer={beatWaveSurfer}
                          takeWaveSurfer={takeWaveSurfer}
                          getBeatAudioUrl={
                            song.audioFileUrl
                              ? () => Promise.resolve(song.audioFileUrl)
                              : null
                          }
                          getTakeAudioUrl={
                            takeManager
                              ? async () => {
                                  const activeTake =
                                    takeManager.getActiveTake();
                                  if (!activeTake) return null;
                                  return takeManager.getTakeUrl(activeTake);
                                }
                              : null
                          }
                          isMobile={isMobile}
                          autoPlay={autoPlayBeat}
                          onAutoPlayChange={setAutoPlayBeat}
                          shiftOffsetSec={
                            takeManager?.getActiveTake()?.shiftOffsetSec || 0
                          }
                        />
                      )}

                    {/* Record / Takes List */}
                    {takesSongId === song.id && takeManager && (
                      <TakesList
                        takes={takeManager.takes}
                        activeTakeNumber={takeManager.activeTakeNumber}
                        isLoadingTakes={takeManager.isLoadingTakes}
                        onSelectTake={takeManager.setActiveTakeNumber}
                        onDeleteTake={takeManager.deleteTake}
                        onRenameTake={takeManager.renameTake}
                        getTakeUrl={takeManager.getTakeUrl}
                        compact={false}
                        isMobile={isMobile}
                        onWaveSurferReady={setTakeWaveSurfer}
                        // Shift
                        onShiftOffsetChange={takeManager.updateShiftOffset}
                        beatDuration={beatDuration}
                        // Loop
                        loopActive={loopActive}
                        onToggleLoop={() => setLoopActive((v) => !v)}
                        onLoopRegionChange={setLoopRegion}
                        // Loop-record
                        loopRecordState={loopRecord.loopRecordState}
                        onStartLoopRecord={loopRecord.startLoopRecord}
                        onStopLoopRecord={loopRecord.stopLoopRecord}
                        onCancelLoopRecord={loopRecord.cancelLoopRecord}
                        onAcceptSplice={async () => {
                          if (loopRecord.splicedBlob && takeManager) {
                            const activeTake = takeManager.getActiveTake();
                            if (activeTake) {
                              try {
                                await takeManager.replaceTakeAudio(
                                  activeTake,
                                  loopRecord.splicedBlob,
                                );
                              } catch (err) {
                                console.error(
                                  "❌ Failed to save loop-record:",
                                  err,
                                );
                              }
                            }
                          }
                          loopRecord.acceptSplice();
                        }}
                        onRetrySplice={loopRecord.retrySplice}
                        loopRecordSplicedBlob={loopRecord.splicedBlob}
                        loopRecordError={loopRecord.error}
                        getAnalyserData={recorder?.getAnalyserData}
                        // Regular recording
                        recorder={recorder}
                        onSaveTake={
                          onSaveTake
                            ? (blob, label, duration) =>
                                onSaveTake(song.id, blob, label, duration)
                            : null
                        }
                      />
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadTab;
